/**
 * Mirror of the API's layout contract (services/api/src/story/formats/layout.types.ts),
 * served by GET /stories/:id/layout. Units are inches from the page's top-left corner;
 * lines arrive already wrapped and paginated, so the page view only places them.
 */

export type LayoutKind = 'screenplay' | 'stage_play' | 'manuscript' | 'poetry';

export type LayoutFont = 'courier' | 'times';

/** The bundled font that draws a line; absent means the role's Latin face. */
export type LayoutFace =
  | 'courier-prime'
  | 'liberation-mono'
  | 'liberation-serif'
  | 'noto-naskh-arabic'
  | 'noto-serif-devanagari'
  | 'noto-serif-sc'
  | 'noto-serif-jp'
  | 'noto-serif-kr';

/** One run in one face and one direction; a mixed line arrives as several, already placed. */
export interface LayoutLine {
  /** Logical (typed) order, even when right-to-left. */
  text: string;
  /** Left edge for left-aligned text; the centre for centred; the right edge for right-aligned. */
  x: number;
  /** Baseline. */
  y: number;
  align: 'left' | 'center' | 'right';
  font: LayoutFont;
  face?: LayoutFace;
  dir?: 'rtl';
  /** Points. */
  size: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

/** A font file the pages draw with; `url` is relative to the API root. */
export interface LayoutFontFile {
  face: LayoutFace;
  bold: boolean;
  italic: boolean;
  url: string;
}

export interface LayoutPage {
  /** 1-based physical page; a title page is page 1 and carries no printed number. */
  index: number;
  printedNumber: string | null;
  titlePage?: boolean;
  lines: LayoutLine[];
}

export interface StoryLayout {
  kind: LayoutKind;
  /** US Letter: 8.5 × 11. */
  pageWidth: number;
  pageHeight: number;
  pages: LayoutPage[];
  /** Body words (title page and headers excluded). */
  wordCount: number;
  fonts?: LayoutFontFile[];
}

/** Fallbacks behind the bundled faces, while they load or if they can't. */
export const FONT_STACKS: Record<LayoutFont, string> = {
  courier: '"Courier Prime", "Courier New", Courier, monospace',
  times: '"Liberation Serif", "Times New Roman", Times, serif',
};

const PRIMARY_FACE: Record<LayoutFont, LayoutFace> = {
  courier: 'courier-prime',
  times: 'liberation-serif',
};

/** Faces the API shapes (joining, conjuncts); every other face prints plain advances. */
const COMPLEX_FACES: ReadonlySet<LayoutFace> = new Set<LayoutFace>([
  'noto-naskh-arabic',
  'noto-serif-devanagari',
]);

const familyOf = (face: LayoutFace): string => `pw-${face}`;

export interface LineFont {
  fontFamily: string;
  /** Latin and CJK faces: no kerning or ligatures, so widths match the API's measure. */
  plain: boolean;
}

/** The CSS font for a line: the bundled face the PDF draws it with, then fallbacks. */
export const lineFont = (line: LayoutLine): LineFont => {
  const stack = FONT_STACKS[line.font] ?? FONT_STACKS.courier;
  const face = line.face ?? PRIMARY_FACE[line.font];
  return {
    fontFamily: face ? `"${familyOf(face)}", ${stack}` : stack,
    plain: !face || !COMPLEX_FACES.has(face),
  };
};

const registeredFonts = new Set<string>();

/**
 * Register the layout's font files (served by the API, the same files the PDF embeds) as
 * font faces. The browser downloads a file only when a line on screen uses it; a style the
 * face lacks is synthesised, as the PDF does.
 */
export const registerFonts = (fonts: LayoutFontFile[] | undefined, apiBase: string): void => {
  if (!fonts || typeof document === 'undefined' || typeof FontFace === 'undefined') return;
  const base = apiBase.replace(/\/+$/, '');
  for (const f of fonts) {
    const src = `${base}/${f.url}`;
    if (registeredFonts.has(src)) continue;
    registeredFonts.add(src);
    document.fonts.add(
      new FontFace(familyOf(f.face), `url("${src}") format("truetype")`, {
        weight: f.bold ? '700' : '400',
        style: f.italic ? 'italic' : 'normal',
        display: 'swap',
      }),
    );
  }
};

/** Where a line's baseline sits below the top of a `line-height: 1` box, in ems. */
export const ASCENT_EM = 0.8;

export interface PlacedLine {
  left: number;
  top: number;
  fontSize: number;
  /** Shifts the box so `left` is its centre or right edge. */
  translateX: '0' | '-50%' | '-100%';
}

/** A layout line in CSS pixels, for a page drawn at `pxPerInch`. */
export const placeLine = (line: LayoutLine, pxPerInch: number): PlacedLine => {
  const fontSize = (line.size / 72) * pxPerInch;
  return {
    left: line.x * pxPerInch,
    top: line.y * pxPerInch - ASCENT_EM * fontSize,
    fontSize,
    translateX: line.align === 'center' ? '-50%' : line.align === 'right' ? '-100%' : '0',
  };
};

/** "About" a word count: to the nearest 10 under a thousand, else the nearest 100. */
export const roundWords = (n: number): number =>
  n < 100 ? n : n < 1000 ? Math.round(n / 10) * 10 : Math.round(n / 100) * 100;
